import { ReactNode } from "react";
import { Link } from "wouter";
import { useStoreQuery, queries } from "@/lib/store";
import { GlassCard, Btn } from "@/components/mnf/UI";

/**
 * Route guard for coach-facing screens.
 *
 * /coach, /coach/compliance, /coach/macros/:menteeId and /coach/notifications
 * shipped with NO role check of any kind. Any signed-in sister who typed the
 * URL saw Kiley's business dashboard — named members, their adherence rows,
 * their macro plans, and their postpartum movement clearance. /coach/macros
 * takes the mentee id straight from the URL, so it was also a read/write
 * handle on another member's plan.
 *
 * CoachReview.tsx already did this correctly with an inline check. Rather
 * than copy that check into four more files, the gate lives here and wraps
 * the route, so the page component never mounts and none of its queries run
 * for a non-coach.
 *
 * This is a CLIENT gate on a client-side store, so it is a privacy fix, not a
 * security boundary — it cannot be, while these pages read the in-memory
 * store instead of Convex. When these screens move to Convex, the server
 * functions must gate on role themselves the way convex/coachReview.ts does.
 * Do not read this component as evidence that the data is protected.
 */
export default function CoachOnly({ children }: { children: ReactNode }) {
  const me = useStoreQuery(queries.currentUser);

  // Identity not resolved yet. Render nothing rather than flashing either the
  // coach dashboard or a "not for you" message at someone mid-boot.
  if (!me) return null;

  if (me.role !== "coach" && me.role !== "admin") {
    return (
      <div style={{ padding: "48px 16px" }}>
        <GlassCard padding={24}>
          <p
            className="mnf-serif"
            style={{ fontSize: 22, color: "var(--mnf-fd)", textAlign: "center" }}
          >
            This one&rsquo;s for coaches.
          </p>
          <p
            className="mnf-body italic mt-2"
            style={{
              fontSize: 14,
              color: "var(--mnf-tg)",
              textAlign: "center",
            }}
          >
            Your own garden is just a tap away.
          </p>
          <div className="mt-5 flex justify-center">
            <Link href="/" className="no-underline">
              <Btn variant="primary" data-testid="button-coachonly-home">
                Back to Today
              </Btn>
            </Link>
          </div>
        </GlassCard>
      </div>
    );
  }

  return <>{children}</>;
}
